import { useMemo, useState } from 'react';
import type { DraftState, TeamLiveData } from '../types';
import type { TeamValuation } from '../lib/valuation';
import { STRUCTURED_TEAMS, TEAMS_BY_ABBR } from '../constants/teams';
import { BASELINE_PROJECTIONS } from '../constants/teamProjections';
import { TeamLogo } from './TeamLogo';
import { TeamDashboardModal } from './TeamDashboard';

interface Props {
  state: DraftState;
  pastPrices: Record<string, number>;
  liveData: Record<string, TeamLiveData>;
  rankings: Record<string, number>;
  valuations: Record<string, TeamValuation>;
  canNominate: boolean;
  onNominate: (abbr: string) => void;
}

export function AvailableTeamsList({
  state, pastPrices, liveData, rankings, valuations, canNominate, onNominate,
}: Props) {
  const [openTeam, setOpenTeam] = useState<string | null>(null);
  const [filter, setFilter] = useState<'available' | 'all'>('available');

  const getAiTag = (abbr: string) => {
    const v = valuations[abbr];
    const fairVal = v?.suggested ?? 8;
    const base = BASELINE_PROJECTIONS[abbr];
    const tier = base?.tier ?? 3;
    if (tier <= 2 && fairVal <= 9) return { label: '🎯 Target', color: 'text-emerald-400' };
    if (tier <= 2 && fairVal >= 12) return { label: '🔥 Bait', color: 'text-rose-400' };
    if (tier >= 3 && fairVal <= 7 && (base?.projectedWins ?? 8) >= 8.4) return { label: '💎 Sleeper', color: 'text-purple-300' };
    return null;
  };

  const visibleByConf = useMemo(() => {
    const isAvail = (abbr: string) => state.availableTeams.includes(abbr);
    const out: typeof STRUCTURED_TEAMS = JSON.parse(JSON.stringify(STRUCTURED_TEAMS));
    return out;
  }, []);

  return (
    <div className="panel p-4 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-3 sticky top-0 bg-brand-panel z-10 pb-2">
        <h2 className="text-lg font-extrabold text-white">
          Teams <span className="text-teal-400">({state.availableTeams.length})</span>
        </h2>
        <div className="flex gap-1 text-xs">
          <button
            onClick={() => setFilter('available')}
            className={`px-2 py-1 rounded ${filter === 'available' ? 'bg-teal-500 text-slate-900 font-bold' : 'bg-slate-700 text-slate-300'}`}
          >Available</button>
          <button
            onClick={() => setFilter('all')}
            className={`px-2 py-1 rounded ${filter === 'all' ? 'bg-teal-500 text-slate-900 font-bold' : 'bg-slate-700 text-slate-300'}`}
          >All 32</button>
        </div>
      </div>

      {(['AFC', 'NFC'] as const).map(conf => (
        <div key={conf} className="mb-4">
          <h3 className="text-sm font-bold text-slate-300 mb-2 border-b border-slate-700 pb-1">
            {conf}
          </h3>
          {(['East', 'North', 'South', 'West'] as const).map(div => {
            const teams = visibleByConf[conf][div].filter(t =>
              filter === 'all' ? true : state.availableTeams.includes(t.abbr),
            );
            if (teams.length === 0) return null;
            return (
              <div key={div} className="mb-3">
                <div className="text-xs text-teal-400 font-semibold mb-1">{div}</div>
                <div className="grid grid-cols-2 gap-2">
                  {teams.map(t => {
                    const taken = !state.availableTeams.includes(t.abbr);
                    const price = pastPrices[t.abbr];
                    const rank = rankings[t.abbr];
                    const v = valuations[t.abbr];
                    const aiTag = !taken ? getAiTag(t.abbr) : null;
                    return (
                      <div
                        key={t.abbr}
                        className={`relative card p-2 flex items-center gap-2 transition ${
                          taken ? 'opacity-40 grayscale' : 'hover:border-teal-400 cursor-pointer'
                        }`}
                        style={{ borderLeft: `4px solid ${t.color}` }}
                      >
                        <button
                          onClick={() => setOpenTeam(t.abbr)}
                          className="flex items-center gap-2 flex-1 text-left min-w-0"
                        >
                          <TeamLogo abbr={t.abbr} size={32} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1">
                              <span className="text-xs font-bold text-white truncate">{t.name}</span>
                              {aiTag && (
                                <span className={`text-[9px] font-black shrink-0 ${aiTag.color}`}>
                                  {aiTag.label}
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-400 flex gap-1 items-center">
                              {v && (
                                <span className="text-emerald-300 font-bold" title="Suggested bid (transparent)">≈${v.suggested}</span>
                              )}
                              {price != null && (
                                <span className="text-amber-400">${price}</span>
                              )}
                              {rank && (
                                <span className="text-slate-500">#{rank}</span>
                              )}
                            </div>
                          </div>
                        </button>
                        {!taken && canNominate && (
                          <button
                            onClick={() => onNominate(t.abbr)}
                            className="text-xs px-2 py-1 bg-teal-500 hover:bg-teal-400 text-slate-950 rounded font-bold shrink-0"
                          >Nominate</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {openTeam && (
        <TeamDashboardModal
          abbr={openTeam}
          liveData={liveData[openTeam]}
          ranking={rankings[openTeam]}
          pastPrice={pastPrices[openTeam]}
          onClose={() => setOpenTeam(null)}
        />
      )}
    </div>
  );
}
